import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { IBizEntity } from '@optima-stride-ai/core';
import { LocalStorageService, OptimaAPPServer2Service, OptimaAPIHeaderService, StateStore, CachedDataCategories, CacheValidationService, iLocalStorageConfig, OptimaAPIs, ProgressIndicatorService, JsonLoaderService, UserContextService, createDefaultOwnerDetail } from '@optima-stride-ai/optima-platform';
import { DocumentMode } from '../../../shared/enums/document-mode.enum';
import { URLTemplates } from '../../../shared/enums/app-constants';
import { environment } from '../../../../environments/environment';

export enum FileMetadataHeaders {
  RANGE_FIRST_BYTE = 'bytes=0-0'
}

export enum HttpMethod {
  GET = 'GET',
  HEAD = 'HEAD'
}

export enum ResponseType {
  ARRAYBUFFER = 'arraybuffer',
  BLOB = 'blob',
  TEXT = 'text',
  JSON = 'json'
}

export enum ObserveType {
  BODY = 'body',
  RESPONSE = 'response'
}

@Injectable({
  providedIn: 'root'
})
/**
 * @deprecated Moved to @optima-stride-ai/optima-app-framework.
 *             Use the library implementation instead.
 */
export class WopiServerStateStoreService {
  private http = inject(HttpClient);
  private optimaAppServerService = inject(OptimaAPPServer2Service);

  private localStorageService = inject(LocalStorageService);
  private cacheInvalidationService = inject(CacheValidationService);
  private jsonLoaderService: JsonLoaderService = inject(JsonLoaderService);
  private progressIndicatorService: ProgressIndicatorService = inject(ProgressIndicatorService);
  private optimaAPIHeaderService = inject(OptimaAPIHeaderService);
  private optimaAPIs = inject(OptimaAPIs);
  private wopiApiStore: StateStore<any> | undefined;

  showDebugMessages = false;

  // private appServerURL = benzaitenAppServerURL;
  // private appHeaders = benzaitenAppHeaders;

  // private readonly apiHeaders : OptimaAPIHeaderValues = new OptimaAPIHeaderValues(this.appHeaders.tenantuuid, this.appHeaders.appid, this.appHeaders.userToken);

  private _dealWithDataLocally = false; //API will not be called, data will be generated locally from the mock data (if the shape of data is not in sync (server/local/app-logic), this might fail)

  constructor() {
    this.initialSetup();
  }

  set dealWithDataLocally(value: boolean) { this._dealWithDataLocally = value; }


  private initialSetup() {
    let localStoreConfig: iLocalStorageConfig = {
      localStorageService: this.localStorageService,
      discardLocalData: !this._dealWithDataLocally,
      initialMockDataJSONAssetRelativePath: 'data/static/startup/test-tenant-two/biz-entity-type-test-tenant-two.json'
    };
    this.wopiApiStore = new StateStore<IBizEntity[]>(CachedDataCategories.All, this.optimaAppServerService, localStoreConfig, this.cacheInvalidationService, this.jsonLoaderService, (data) => { return true; }, this.optimaAPIHeaderService, BenBizEntityAPIDataTransformer, this.progressIndicatorService, true);
    this.wopiApiStore.showDebugMessages = this.showDebugMessages;
    }

  get changed() {
    return this.wopiApiStore?.changed;
  }

  getIframeUrl(
    filename?: string,
    mode?: DocumentMode,
    userId?: string,
    userName?: string,
    tenantId?: string
  ): Promise<unknown> {

    if (!this.wopiApiStore) throw new Error("Store not initialized");
    if (this._dealWithDataLocally) {
      return this.wopiApiStore.getState()
    } else {
      // return new Promise<unknown>((resolve, reject) => {
      //   this.wopiApiStore?.getState(
      //     {
      //       authRequired: true,
      //       apiEndPoint: this.optimaAPIs.collobaraIFrameURL,
      //       apiParams: new HttpParams(
      //         {
      //           fromString: buildIframeQueryString(filename, mode, userId, userName, tenantId)
      //         }
      //       ),
      //       fullURLWithParams: '',
      //       method: 'GET',
      //       headers: [],
      //       body: undefined
      //     }, "").then((data) => {
      //       resolve(data);
      //     }).catch((error) => {
      //       reject(error);
      //     });
      // })
      const queryString = buildIframeQueryString(filename, mode, userId, userName, tenantId);
      const url = `${environment.apiUrl}/collabora/iframe-url${queryString ? `?${queryString}` : ''}`;
      return firstValueFrom(this.http.get(url));
    };

  }

  /**
   * Generate thumbnail for a Collabora file
   * @param formData - FormData containing the file to generate thumbnail for
   * @returns Promise with ArrayBuffer containing the thumbnail image data
   */
  getCollaboraThumbnail(formData: FormData): Promise<ArrayBuffer> {

    if (!this.wopiApiStore) throw new Error("Store not initialized");

    if (this._dealWithDataLocally) {
      // Return mock ArrayBuffer for local development
      return Promise.resolve(new ArrayBuffer(0));
    }

    return new Promise<ArrayBuffer>((resolve, reject) => {
    
      this.wopiApiStore?.create(
        {
          authRequired: true,
          apiEndPoint: this.optimaAPIs.collobaraGenerateThumbnail,
          apiParams: new HttpParams(),
          fullURLWithParams: '',
          method: 'POST',
          headers: [
            { key: 'Accept', value: 'image/png, image/jpeg, application/octet-stream, arraybuffer' }
          ],
          body: formData
        }, "GenerateThumbnail", true
      ).then((data: any) => {
        const responseData = data.data.data;
        
        // The response should be ArrayBuffer
        if (responseData instanceof ArrayBuffer) {
          resolve(responseData);
        } else if (responseData && typeof responseData === 'object' && responseData.buffer) {
          // Handle case where data might be wrapped
          resolve(responseData.buffer);
        } else if (Array.isArray(responseData)) {
          // Handle case where response is a byte array
          const uint8Array = new Uint8Array(responseData);
          resolve(uint8Array.buffer);
        } else {
          console.error("Unexpected response type for thumbnail:", typeof responseData);
          reject(new Error("Invalid response type for thumbnail generation"));
        }
      }).catch((error) => {
        console.error("Collabora Thumbnail generation failed:", error);
        reject(error);
      });
    });
  }

  /**
   * Generic method to download file with configurable response type and headers
   * @param fileUrl - The URL of the file to download
   * @param options - Configuration options for the download
   * @param options.responseType - Type of response: 'arraybuffer', 'blob', 'text', or 'json'
   * @param options.headers - Optional custom headers
   * @param options.observe - Whether to observe 'body' or 'response' (default: 'body')
   * @returns Promise with the downloaded data in the specified format
   */
  async downloadFile<T = any>(
    fileUrl: string,
    options: {
      responseType: ResponseType;
      headers?: { [key: string]: string };
      observe?: ObserveType;
    }
  ): Promise<T> {
    const httpHeaders = options.headers ? new HttpHeaders(options.headers) : undefined;
    const observe = options.observe || ObserveType.BODY;

    return await firstValueFrom(
      this.http.get(fileUrl, {
        responseType: options.responseType as any,
        headers: httpHeaders,
        observe: observe,
      } as any) as any
    );
  }

  /**
   * Generic method to get file metadata with configurable method and headers
   * @param fileUrl - The URL of the file
   * @param options - Configuration options for metadata retrieval
   * @param options.method - HTTP method: 'GET' (with Range) or 'HEAD'
   * @param options.headers - Optional custom headers
   * @returns Promise with HttpResponse containing headers and optional content
   */
  async getFileMetadata(
    fileUrl: string,
    options: {
      method: HttpMethod;
      headers?: { [key: string]: string };
    }
  ): Promise<HttpResponse<any>> {
    const defaultHeaders: { [key: string]: string } = options.method === HttpMethod.GET 
      ? { 'Range': FileMetadataHeaders.RANGE_FIRST_BYTE } 
      : {};
    const headers = new HttpHeaders({ ...defaultHeaders, ...(options.headers || {}) });

    if (options.method === HttpMethod.HEAD) {
      return await firstValueFrom(
        this.http.head(fileUrl, {
          observe: 'response',
          headers,
        })
      );
    } else {
      return await firstValueFrom(
        this.http.get(fileUrl, {
          headers,
          observe: 'response',
          responseType: 'arraybuffer',
        })
      );
    }
  }


}


export function buildIframeQueryString(
  filename?: string,
  mode?: DocumentMode,
  userId?: string,
  userName?: string,
  tenantId?: string
): string {
  const params = new URLSearchParams();

  // Add iframe URL parameters
  if (filename) {
    params.append('filename', filename);
  }

  if (mode) {
    params.append('mode', mode);
  }

  if (userId) {
    params.append('userId', userId);
  }

  if (userName) {
    params.append('userName', userName);
  }

  if (tenantId) {
    params.append('tenantId', tenantId);
  }

  return params.toString();
}

export function BenBizEntityAPIDataTransformer(data: any): IBizEntity[] {
  if (!Array.isArray(data)) {
    return data;
  }

  if (data) {
    data = data.map((item: any) => {
      if (item.hasOwnProperty('icon')) {
        if (!item.icon.startsWith("cil")) {
          item.icon = "cilFlagAlt";
        }
      }
      // if(!item.hasOwnProperty('ownerDetail')){
      //   item.ownerDetail = {
      //     "id": item.ownerId,
      //     "parentId": "",
      //     "tenantId": "0000",
      //     "appId": "0001",
      //     "name": "Steven King",
      //     "shortName": "Steven",
      //     "avatar": getAvatarUrlFromEmail(item.createdBy || ''),
      //   };
      //   item.ownerId = item.ownerDetail.id;
      // }

      if (!item.hasOwnProperty('ownerDetail')) {
        if (!item.hasOwnProperty('owner')) {
          item.ownerDetail = createDefaultOwnerDetail(item.createdBy)
        }
        else {
          item.ownerDetail = {
            id: item.ownerId,
            name: item.owner,
            avatar: getAvatarUrlFromEmail(item.owner || '')
          }
        }
      }

      return item;
    });
  }
  return data;
}



export function getAvatarUrlFromEmail(email: string) {
  const nameFromEmail = email.split('@')[0].replace(/[._]/g, ' '); // e.g., john_doe -> John Doe
  const formattedName = nameFromEmail
    .split(' ')
    .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  return `${URLTemplates.AVATAR_API_ENDPOINT}${encodeURIComponent(formattedName)}`;
}